import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { BackupScheduleDTO } from 'src/app/model/backup-schedule-dto';
import { BackupStorageDeviceDTO } from 'src/app/model/backup-storage-device-dto';
import { BackupService } from 'src/app/services/backup.service';
import { cloneDeep } from 'lodash'

@Component({
    selector: 'app-edit-backup-schedule',
    standalone: true,
    imports: [CommonModule, FormsModule, MatIconModule, MatCheckboxModule],
    templateUrl: './edit-backup-schedule.component.html',
    styleUrl: './edit-backup-schedule.component.scss',
})
export class EditBackupScheduleComponent {
    public daysOfWeek: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    public minutesOfHour: number[] = Array.from({ length: 60 }, (_, i) => i);
    public editMode: boolean = false
    public working: boolean = false
    public storageDevices: BackupStorageDeviceDTO[] = []
    public schedule: BackupScheduleDTO = {
        hour: 0,
        minute: 0,
        opened: false,
        days: []
    };

    constructor(
        public backupService: BackupService,
        public dialogRef: MatDialogRef<Component>,
        @Inject(MAT_DIALOG_DATA) public data: BackupScheduleDTO,
        private readonly toastr: ToastrService
    ) {
        this.editMode = data?.id != null

        this.getBackupStorageDevices()

        if (this.editMode) {
            this.schedule = cloneDeep(data);
        }
    }

    getBackupStorageDevices() {
        this.backupService.getBackupDevices().then(response => {
            this.storageDevices = response.items;
            this.storageDevices.forEach(x => x.schedules = [])
        })
    }

    getScheduleStorageId(): number {
        return this.schedule.storageDevice?.id ?? -1
    }

    toggleMarkAllScheduleDays(): void {
        if (!this.allScheduleDaysSelected()) {
            this.schedule.days = [...Array(7).keys()]
        } else {
            this.schedule.days = []
        }
    }

    allScheduleDaysSelected(): boolean {
        return (this.schedule.days?.length ?? 0) >= this.daysOfWeek.length
    }

    changeScheduleStorage(id: number): void {
        this.schedule.storageDevice = cloneDeep(this.storageDevices.find(x => x.id === id))
    }

    public isScheduleDayActive(schedule: BackupScheduleDTO, dayIndex: number): boolean {
        return schedule?.days?.findIndex(x => x == dayIndex) !== -1
    }

    public toggleScheduleDay(schedule: BackupScheduleDTO, dayIndex: number): void {
        let activeIndex = schedule?.days?.findIndex(x => x == dayIndex) ?? -1

        if (activeIndex === -1) {
            schedule.days?.push(dayIndex)
        } else {
            schedule.days?.splice(activeIndex, 1)
        }
    }

    public validSchedule(): boolean {
        if (this.schedule.name == null) return false;
        if (this.schedule.path == null) return false;
        if (this.schedule.hour == null) return false;
        if (this.schedule.minute == null) return false;
        if (this.schedule.days == null) return false;

        return this.schedule != null &&
            this.schedule.name?.length > 0 &&
            this.schedule.path?.length > 0 &&
            this.schedule.hour >= 0 && this.schedule.hour <= 23 &&
            this.schedule.minute >= 0 && this.schedule.minute <= 59 &&
            this.schedule.days?.length > 0 &&
            this.schedule.storageDevice?.id != null
    }

    public save(): void {
        this.working = true

        let action = this.editMode ?
            this.backupService.updateBackupSchedule(this.schedule)
            : this.backupService.createBackupSchedule(this.schedule)

        action
            .then(response => {
                this.toastr.success('Schedule saved!')
                this.dialogRef.close(true)
            })
            .catch(error => {
                this.toastr.error('Error saving this schedule.')
            })
            .finally(() => {
                this.working = false
            })
    }

    public cancel(): void {
        this.dialogRef.close()
    }
}
